import {
  Bell,
  BellSlash,
  CheckCircle,
  Info,
  WarningCircle,
} from '@phosphor-icons/react'
import { useId } from 'react'
import { useTranslation } from 'react-i18next'

import { LoadingState } from '@/components/loading-state'

import type { WebPushState } from '../hooks/use-web-push'
import { NotificationNotice } from './notice'

interface PushCardProps {
  state: WebPushState
}

interface PushInfoProps {
  tone: 'info' | 'error'
  titleKey: string
  bodyKey: string
  onRetry?: () => void
}

function PushInfo({ tone, titleKey, bodyKey, onRetry }: PushInfoProps) {
  const { t } = useTranslation()
  const Icon = tone === 'error' ? WarningCircle : Info
  const classes =
    tone === 'error'
      ? 'border-[var(--danger)] bg-[var(--danger-soft)] text-danger'
      : 'border-line bg-mint text-teal'

  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={`mt-3 flex flex-wrap items-start gap-3 rounded-md border px-4 py-3 ${classes}`}
    >
      <Icon size={20} weight="bold" aria-hidden="true" className="mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-bold">{t(titleKey)}</p>
        <p className="mt-1 text-sm leading-relaxed">{t(bodyKey)}</p>
      </div>
      {onRetry ? (
        <button type="button" className="btn-ghost min-h-11 px-2" onClick={onRetry}>
          {t('common.retry')}
        </button>
      ) : null}
    </div>
  )
}

/**
 * Browser push controls. The only path to `Notification.requestPermission()`
 * is the explicit enable button; the card otherwise reports unsupported,
 * blocked, server-disabled or already-subscribed states. It never renders the
 * VAPID key, endpoint or subscription keys.
 */
export function PushCard({ state }: PushCardProps) {
  const { t } = useTranslation()
  const headingId = useId()
  const {
    supported,
    permission,
    subscribed,
    keyState,
    busy,
    status,
    enable,
    disable,
    refresh,
    dismissStatus,
  } = state

  const enabling = busy === 'enabling'
  const disabling = busy === 'disabling'
  const canEnable =
    supported &&
    permission !== 'denied' &&
    keyState === 'available' &&
    !subscribed

  return (
    <section
      aria-labelledby={headingId}
      className="mt-5 rounded-card border border-line bg-white p-4 md:p-5"
    >
      <h2 id={headingId} className="text-xl">
        {t('notifications.push.title')}
      </h2>
      <p className="mt-1 max-w-[60ch] text-sm leading-relaxed text-muted">
        {t('notifications.push.body')}
      </p>

      {!supported ? (
        <PushInfo
          tone="info"
          titleKey="notifications.push.unsupportedTitle"
          bodyKey="notifications.push.unsupportedBody"
        />
      ) : permission === 'denied' ? (
        <PushInfo
          tone="error"
          titleKey="notifications.push.deniedTitle"
          bodyKey="notifications.push.deniedBody"
        />
      ) : keyState === 'missing' ? (
        <PushInfo
          tone="info"
          titleKey="notifications.push.disabledTitle"
          bodyKey="notifications.push.disabledBody"
        />
      ) : keyState === 'error' ? (
        <PushInfo
          tone="error"
          titleKey="notifications.push.unavailableTitle"
          bodyKey="notifications.push.unavailableBody"
          onRetry={refresh}
        />
      ) : keyState === 'checking' ? (
        <LoadingState labelKey="notifications.push.checking" compact />
      ) : (
        <>
          <p className="mt-3 flex items-center gap-2 text-sm font-bold text-teal">
            {subscribed ? (
              <CheckCircle size={18} weight="bold" aria-hidden="true" />
            ) : (
              <BellSlash size={18} weight="bold" aria-hidden="true" />
            )}
            {subscribed
              ? t('notifications.push.enabled')
              : t('notifications.push.inactive')}
          </p>

          {canEnable ? (
            <div className="mt-3">
              <button
                type="button"
                className="btn-primary min-h-11 px-5 text-sm"
                onClick={() => void enable()}
                disabled={enabling}
                aria-busy={enabling}
              >
                <Bell size={18} weight="bold" aria-hidden="true" />
                {enabling
                  ? t('notifications.push.enabling')
                  : t('notifications.push.enable')}
              </button>
              <p className="mt-2 text-xs leading-relaxed text-muted">
                {t('notifications.push.permissionHint')}
              </p>
            </div>
          ) : null}

          {subscribed ? (
            <button
              type="button"
              className="btn-secondary mt-3 min-h-11 px-5 text-sm"
              onClick={() => void disable()}
              disabled={disabling}
              aria-busy={disabling}
            >
              <BellSlash size={18} weight="bold" aria-hidden="true" />
              {disabling
                ? t('notifications.push.disabling')
                : t('notifications.push.disable')}
            </button>
          ) : null}
        </>
      )}

      {status ? (
        <div className="mt-3">
          <NotificationNotice
            tone={status.tone}
            messageKey={status.messageKey}
            onDismiss={dismissStatus}
          />
        </div>
      ) : null}
    </section>
  )
}
